<template>
  <div :data-id="id" class="flex flex-col gap-4">
    <div :data-id="id" class="flex items-center gap-3">
      <div :data-id="id" class="w-25 flex-shrink-0 rounded shadow-md">
        <div
          :data-id="id"
          class="h-7 flex items-center justify-center rounded-t bg-orange-200"
        >
          <BlTextBody2
            :data-id="id"
            :text="displayDate(appointment.date, 'dddd')"
            class="text-orange-700"
          />
        </div>
        <div
          :data-id="id"
          class="h-18 flex flex-col items-center justify-center rounded-b bg-pure-white"
        >
          <BlTextTitle4
            :data-id="id"
            :text="displayDate(appointment.date, 'DD')"
            class="text-orange-700"
          />
          <BlTextBody2
            :data-id="id"
            :text="displayDate(appointment.date, 'MMMM')"
            class="text-orange-600"
          />
        </div>
      </div>
      <div :data-id="id" class="py-3 px-3 flex flex-col">
        <div :data-id="id" class="max-w-54 mb-1 flex items-center gap-1">
          <BlTextBody2 :data-id="id" text="Therapist:" />
          <BlTextSubtitle3
            v-if="!appointment.user"
            :data-id="id"
            text="-"
            class="truncate"
          />
          <BlTextSubtitle3
            v-else
            :data-id="id"
            :title="appointment.user.name || 'Deleted user'"
            :text="appointment.user.name || 'Deleted user'"
            class="truncate"
            :class="{ 'italic ': !appointment.user.name }"
          />
        </div>
        <BlTextTitle4
          :data-id="id"
          :text="
            `${appointment.start_time_string} - ${appointment.end_time_string}`
          "
          class="mb-3 text-slate-800"
        />
        <div :data-id="id" class="flex items-center gap-2">
          <div :data-id="id" class="max-w-28 flex items-center gap-2">
            <div
              v-if="enableBranch && appointment.room.branch"
              :data-id="id"
              class="h-7 min-w-12 max-w-16 px-2 flex items-center justify-center rounded truncate"
              :style="{
                backgroundColor:
                  appointment.room.branch.background_color || 'unset'
              }"
              :title="appointment.room.branch.name"
            >
              <BlTextBody2
                :data-id="id"
                class="truncate"
                :text="appointment.room.branch.name || '-'"
                :style="{
                  color: appointment.room.branch.color || 'unset'
                }"
              />
            </div>
            <div :data-id="id" class="truncate" :title="appointment.room.name">
              <BlTextBody2
                :data-id="id"
                :text="appointment.room.name"
                class="truncate"
              />
            </div>
          </div>
          <div
            :data-id="id"
            class="w-1 h-1 flex-shrink-0 bg-slate-600 rounded"
          ></div>
          <BlChip :data-id="id" :chip="appointment.status" />
        </div>
      </div>
      <div
        v-if="useAction"
        :data-id="id"
        class="relative ml-auto flex items-center"
      >
        <BlInputButton
          :data-id="id"
          type="outlined"
          icon="dots-vertical"
          dense
          @clicked="menuOpened = !menuOpened"
        />
        <div
          v-if="menuOpened"
          :data-id="id"
          class="absolute z-50 mr-4 top-0 right-full flex flex-col gap-1 bg-pure-white rounded border border-slate-400 p-2"
        >
          <router-link
            :data-id="id"
            :to="`/appointments/details/${appointment.id}`"
            target="_blank"
            class="px-4 py-2 hover:bg-hover-state text-left min-w-56 outline-none rounded transition-all duration-300"
          >
            <BlTextBody2
              :data-id="id"
              class="text-slate-1000"
              text="View appointment details"
            />
          </router-link>
          <router-link
            v-if="asHeader"
            :data-id="id"
            :to="`/clients/${appointment.client.id}`"
            target="_blank"
            class="px-4 py-2 hover:bg-hover-state text-left min-w-56 outline-none rounded transition-all duration-300"
          >
            <BlTextBody2
              :data-id="id"
              class="text-slate-1000"
              text="View profile"
            />
          </router-link>
          <button
            v-if="currentUser.role.includes('admin') && !asHeader"
            :data-id="id"
            @click="$emit('open-reschedule')"
            class="px-4 py-2 text-left min-w-56 outline-none rounded transition-all duration-300"
            :class="[
              appointment.status !== 'scheduled'
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-hover-state'
            ]"
            :disabled="appointment.status !== 'scheduled'"
          >
            <BlTextBody2
              :data-id="id"
              class="text-slate-1000"
              text="Reschedule appointment"
            />
          </button>
          <button
            v-if="currentUser.role.includes('admin') && !asHeader"
            :data-id="id"
            @click="$emit('open-cancellation')"
            class="px-4 py-2 text-left min-w-56 outline-none rounded transition-all duration-300"
            :class="[
              appointment.status === 'cancelled' ||
              appointment.status === 'completed'
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-hover-state'
            ]"
            :disabled="
              appointment.status === 'cancelled' ||
                appointment.status === 'completed'
            "
          >
            <BlTextBody2
              :data-id="id"
              class="text-error-400"
              text="Cancel appointment"
            />
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="currentUser.role.includes('admin') && asHeader"
      :data-id="id"
      class="grid grid-cols-2 gap-4"
    >
      <BlInputButton
        :data-id="id"
        type="outlined-error"
        dense
        :disabled="
          appointment.status === 'cancelled' ||
            appointment.status === 'completed'
        "
        label="Cancel appointment"
        @clicked="$emit('open-cancellation')"
      />
      <BlInputButton
        :data-id="id"
        type="outlined"
        dense
        :disabled="appointment.status !== 'scheduled'"
        @clicked="$emit('open-reschedule')"
        label="Reschedule"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlIcon from "../../BlIcon.vue";
import BlChip from "../../BlChip.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "SlotDetails",
  components: {
    BlTextTitle4,
    BlTextSubtitle1,
    BlTextSubtitle3,
    BlTextBody2,
    BlTextBody3,
    BlIcon,
    BlChip,
    BlInputButton
  },
  props: {
    id: { type: String },
    appointment: { type: Object, default: null },
    asHeader: { type: Boolean, default: false },
    useAction: { type: Boolean, default: false }
    // @open-cancellation
    // @open-reschedule
  },
  data() {
    return {
      menuOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    }
  },
  watch: {
    menuOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    }
  },
  mounted() {},
  methods: {
    ...mapActions([""]),
    displayDate(date, format) {
      return moment(date).format(format);
    },
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    },
    closeHandler() {
      window.removeEventListener("click", this.closeHandler);
      this.menuOpened = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/appointments/timeslot/SlotDetails.vue